// unitComponents.js
// This file is used to create the components for the units tab in the DelForm

import React, { useEffect, useRef } from "react";
import Select from "react-select";
import {
  DEL_UNIT_TYPE_OPTIONS,
  MISC_QUANTITY_TYPE_OPTIONS,
  RENTAL_QUANTITY_TYPE_OPTIONS,
} from "../../../../constants/delConstants";
import {
  positionSelectStyles,
  quantityTypeSelectStyles,
} from "../../../common/delSelect";
import styles from "./UnitComponents.module.css";
import { useAppStore } from "../../../../store/appStore";

// Compute touch-aware React Select props to avoid iOS virtual keyboard detaching menus
const useTouchAwareSelectProps = () => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  if (isTouch) {
    return {
      menuPosition: "absolute",
      menuPlacement: "auto",
      menuShouldScrollIntoView: false,
      menuPortalTarget: portalTarget,
      styles: {
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    };
  }
  return {
    menuPosition: "fixed",
    menuPortalTarget: portalTarget,
    styles: {
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };
};

// Unit row component. This will be its own table row, holding the units for a specific tab.
// rowUnit is the unit that is being edited.
// tabSearchUnits is the list of specific units/unit type that are being searched for.
export const UnitsRow = ({ rowUnit, callbacks, removeUnitRef }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  // Getting the searchable units for the unit selection dropdown,
  // filtering out units already selected in other rows to prevent duplicates
  const allSearchableUnits =
    callbacks.allUnits[callbacks.activeUnitView.tabSearchUnits];
  const selectedUnitIds = new Set(
    callbacks.delData.units
      .filter((u) => u.rowId !== rowUnit.rowId && u.unitId != null)
      .map((u) => u.unitId)
  );
  const searchableUnits = allSearchableUnits.filter(
    (unit) => !selectedUnitIds.has(unit.unit_id)
  );

  // Getting the errors for this specific unit row
  const unitErrors =
    callbacks?.delData?.errors?.units?.find(
      (error) => error.unitRowId === rowUnit.rowId
    )?.errors || {};

  // Get autosave row errors for this unit
  const autosaveRowError = callbacks.getAutosaveRowError?.(rowUnit.rowId);
  const autosaveFieldErrors = autosaveRowError?.field_errors || {};

  // Helper function to get autosave field error
  const getAutosaveFieldError = (fieldName) => {
    return autosaveFieldErrors[fieldName] || null;
  };

  // Handling removing a unit from our table
  const handleRemoveUnit = () => {
    removeUnitRef.current = true;
    callbacks.removeUnit(rowUnit.rowId, callbacks.emitPatch);
  };

  // Handling the selected unit function
  const handleSelectedUnit = (selected) => {
    removeUnitRef.current = true;
    callbacks.setSelectedUnit(
      rowUnit.rowId,
      selected?.label || null,
      selected?.value || null,
      selected?.plural_label || null,
      selected?.unit_type || callbacks.activeUnitView.tabSearchUnits,
      selected?.unit_type_name || null,
      callbacks.emitPatch
    );
  };

  // Handling the unit quantity input
  const handleUnitQuantityInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    const value = parseFloat(e.target.value);
    if (value < 0) {
      callbacks.updateUnitQuantity(
        rowUnit.rowId,
        0,
        sendDataToBackend,
        callbacks.emitPatch
      );
    } else {
      callbacks.updateUnitQuantity(
        rowUnit.rowId,
        value,
        sendDataToBackend,
        callbacks.emitPatch
      );
    }
  };

  return (
    <tr key={rowUnit.rowId} className={styles.unitRow}>
      {/* Putting in data label are for css purposes to display the unit name in the unit selection row on mobile screens */}
      <td data-label="Unit Name">
        <div className={styles.unitInputContainer}>
          <Select
            value={
              rowUnit
                ? {
                    value: rowUnit.unitId,
                    label: rowUnit.unitName,
                    plural_label: rowUnit.unitPlural,
                    unit_type: rowUnit.unitType,
                    unit_type_name: rowUnit.unitTypeName,
                  }
                : "Select Unit"
            }
            onChange={handleSelectedUnit}
            options={searchableUnits.map((unit) => ({
              value: unit.unit_id,
              label: unit.label,
              plural_label: unit.plural_label,
              unit_type: unit.unit_type,
              unit_type_name: unit.unit_type_name,
            }))}
            isDisabled={callbacks.delMode === "view"}
            placeholder={
              unitErrors?.unitId ? (
                <span className={styles.errorText}>{unitErrors.unitId}</span>
              ) : (
                "Search unit..."
              )
            }
            isClearable={true}
            isSearchable={true}
            styles={{
              ...positionSelectStyles(
                !!unitErrors?.unitId || !!getAutosaveFieldError("unit_id")
              ),
              ...(touchSelectProps.styles || {}),
            }}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            className={
              unitErrors?.unitId || getAutosaveFieldError("unit_id")
                ? styles.errorSelectContainer
                : ""
            }
          />
          {getAutosaveFieldError("unit_id") && (
            <span className={styles.fieldErrorText}>
              {getAutosaveFieldError("unit_id")}
            </span>
          )}
        </div>
      </td>
      {/* Middle row with quantity input quantity type and unit type, mobile display */}
      <td className={styles.middleRow}>
        <div className={styles.quantityInputContainer} data-label="Quantity">
          <input
            type="number"
            className={`${styles.quantityInput} ${
              unitErrors?.billableUnits ||
              getAutosaveFieldError("billable_units")
                ? styles.inputError
                : ""
            }`}
            value={rowUnit?.billableUnits || ""}
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={handleUnitQuantityInput}
            onBlur={(e) => {
              handleUnitQuantityInput(e, true);
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const pastedValue = parseFloat(e.clipboardData.getData("text"));
              if (pastedValue < 0) {
                e.preventDefault();
              }
            }}
            disabled={callbacks.delMode === "view"}
            min="0"
          />
          {getAutosaveFieldError("billable_units") && (
            <span className={styles.fieldErrorText}>
              {getAutosaveFieldError("billable_units")}
            </span>
          )}
        </div>
        <div className={styles.quantityTypeContainer} data-label="Bill Type">
          <span className={styles.quantityType}>
            {rowUnit?.unitPlural?.toUpperCase() || ""}
          </span>
        </div>
        <div className={styles.unitTypeContainer} data-label="Unit Type">
          <span className={styles.unitTypeName}>
            {rowUnit?.unitTypeName?.toUpperCase() || ""}
          </span>
        </div>
      </td>
      {callbacks.delMode !== "view" && (
        <td className={styles.actionsRow}>
          <span className={styles.actionsLabel}>Actions</span>
          <button onClick={handleRemoveUnit} className={styles.removeButton}>
            Remove
          </button>
        </td>
      )}
    </tr>
  );
};

// Creating a UnitViewSelector component for users to select from a dropdown list of of units types to view and choose a unit type from. This will be used for the UnitTable component.
export const UnitViewSelector = ({ callbacks }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  // Handling the unit view action
  const handleUnitView = (selected) => {
    callbacks.setUnitActiveUnitsView(selected.value, selected.value);
  };

  return (
    <div className={styles.unitViewSelector}>
      <Select
        value={DEL_UNIT_TYPE_OPTIONS.find(
          (option) => option.value === callbacks.activeUnitView.tabSearchUnits
        )}
        onChange={(selected) => {
          handleUnitView(selected);
        }}
        options={DEL_UNIT_TYPE_OPTIONS}
        className={styles.viewSelect}
        classNamePrefix="unit-view"
        isSearchable={false}
        placeholder="Select a view..."
        menuPortalTarget={touchSelectProps.menuPortalTarget}
        menuPosition={touchSelectProps.menuPosition}
        menuPlacement={touchSelectProps.menuPlacement}
        menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
        styles={{
          ...(touchSelectProps.styles || {}),
        }}
        isDisabled={callbacks.delMode === "view"}
      />
    </div>
  );
};

// Unit table component. This will be the table that contains the units for a specific tab. If allUnitSearch is true
export const UnitTable = ({ callbacks, showAll = false }) => {
  const tableWrapperRef = useRef(null);
  // Putting this here to watch for changes in the remove unit button, so we don't scroll to the bottom of the table when it's clicked.
  const removeUnitRef = useRef(null);
  // Adding this to keep the table from scrolling when we pop in and out of the unit table.
  const initialRender = useRef(true);
  // Anchor to ensure we scroll after DOM updates
  const bottomAnchorRef = useRef(null);

  // Watching unit rows for changes, and scrolling to the bottom anchor when a unit is added.
  useEffect(() => {
    if (showAll) {
      return;
    }
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (!removeUnitRef.current && callbacks.activeTab !== "All") {
      bottomAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
    // reset the flag so subsequent operations don't block scrolls
    removeUnitRef.current = null;
  }, [callbacks.delData.units.length]);

  useEffect(() => {
    return () => {
      initialRender.current = true;
    };
  }, [callbacks.activeUnitView.tabSearchUnits]);

  // Handling adding a new blank unit action
  const handleAddNewUnit = () => {
    removeUnitRef.current = false;
    callbacks.addNewUnit(
      callbacks.activeUnitView.tabSearchUnits,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.unitTableContainer}>
      <UnitViewSelector callbacks={callbacks} />
      <div className={styles.tableWrapper} ref={tableWrapperRef}>
        <table className={styles.unitTable}>
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Unit Name</th>
              <th style={{ width: "45%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ width: "33.33%" }}>Quantity</div>
                  <div style={{ width: "33.33%" }}>Bill Type</div>
                  <div style={{ width: "33.33%" }}>Unit Type</div>
                </div>
              </th>
              {callbacks.delMode !== "view" && (
                <th style={{ width: "20%" }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {callbacks.delData.units &&
              callbacks.delData.units.map((unit) =>
                callbacks.allUnitSearch ? (
                  <UnitsRow
                    rowUnit={unit}
                    callbacks={callbacks}
                    removeUnitRef={removeUnitRef}
                    key={unit.rowId}
                  />
                ) : (
                  unit.unitType === callbacks.activeUnitView.tabSearchUnits && (
                    <UnitsRow
                      rowUnit={unit}
                      callbacks={callbacks}
                      removeUnitRef={removeUnitRef}
                      key={unit.rowId}
                    />
                  )
                )
              )}
          </tbody>
        </table>
        <div ref={bottomAnchorRef} />
      </div>

      <div className={styles.bottomWrapper}>
        {callbacks.delMode !== "view" && (
          <button onClick={handleAddNewUnit} className={styles.addUnitButton}>
            Add EWI Unit
          </button>
        )}
      </div>
    </div>
  );
};

// Misc Equipment Row
export const MiscEquipRow = ({ rowUnit, callbacks, removeUnitRef }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  // Find errors for this specific misc unit row
  const miscUnitErrors =
    callbacks?.delData?.errors?.miscUnits?.find(
      (error) => error.miscEquipmentRowId === rowUnit.rowId
    )?.errors || {};

  // Get autosave row errors for this misc unit
  const autosaveRowError = callbacks.getAutosaveRowError?.(rowUnit.rowId);
  const autosaveFieldErrors = autosaveRowError?.field_errors || {};

  // Helper function to get autosave field error
  const getAutosaveFieldError = (fieldName) => {
    return autosaveFieldErrors[fieldName] || null;
  };

  // Handling removing a misc unit from our table
  const handleRemoveMiscUnit = () => {
    removeUnitRef.current = true;
    callbacks.removeMiscUnit(rowUnit.rowId, callbacks.emitPatch);
  };

  // Handling the misc unit name input
  const handleMiscUnitNameInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    callbacks.updateMiscUnitName(
      rowUnit.rowId,
      e.target.value,
      sendDataToBackend,
      callbacks.emitPatch
    );
  };

  // Handling the misc unit quantity input
  const handleMiscUnitQuantityInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    const value = parseFloat(e.target.value);
    if (value < 0) {
      callbacks.updateMiscUnitQuantity(
        rowUnit.rowId,
        0,
        sendDataToBackend,
        callbacks.emitPatch
      );
    } else {
      callbacks.updateMiscUnitQuantity(
        rowUnit.rowId,
        value,
        sendDataToBackend,
        callbacks.emitPatch
      );
    }
  };

  // Handling the misc unit quantity type input
  const handleMiscUnitQuantityTypeInput = (selected) => {
    removeUnitRef.current = true;
    callbacks.updateMiscUnitQuantityType(
      rowUnit.rowId,
      selected?.value || null,
      callbacks.emitPatch
    );
  };

  // Handling the misc unit vendor input
  const handleMiscUnitVendorInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    callbacks.updateMiscUnitVendor(
      rowUnit.rowId,
      e.target.value,
      sendDataToBackend,
      callbacks.emitPatch
    );
  };

  return (
    <>
      <tr key={rowUnit.rowId} className={styles.unitRow}>
        <td data-label="Item Name">
          <div className={styles.unitInputContainer}>
            <input
              type="text"
              className={`${styles.unitInput} ${
                miscUnitErrors?.unitName || getAutosaveFieldError("unit_name")
                  ? styles.inputError
                  : ""
              }`}
              value={rowUnit?.unitName || ""}
              onChange={handleMiscUnitNameInput}
              onBlur={(e) => {
                handleMiscUnitNameInput(e, true);
              }}
              disabled={callbacks.delMode === "view"}
              placeholder={
                miscUnitErrors?.unitName
                  ? miscUnitErrors.unitName
                  : "Item Name..."
              }
            />
            {getAutosaveFieldError("unit_name") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("unit_name")}
              </span>
            )}
          </div>
        </td>
        <td className={styles.middleRow}>
          <div className={styles.quantityInputContainer} data-label="Quantity">
            <input
              type="number"
              className={`${styles.quantityInput} ${
                miscUnitErrors?.quantity || getAutosaveFieldError("quantity")
                  ? styles.inputError
                  : ""
              }`}
              value={rowUnit?.quantity || ""}
              onFocus={(e) => {
                e.target.select();
              }}
              onChange={handleMiscUnitQuantityInput}
              onBlur={(e) => {
                handleMiscUnitQuantityInput(e, true);
              }}
              onPaste={(e) => {
                const pastedValue = parseFloat(e.clipboardData.getData("text"));
                if (pastedValue < 0.01) {
                  e.preventDefault();
                }
              }}
              disabled={callbacks.delMode === "view"}
              min="0"
            />
            {getAutosaveFieldError("quantity") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("quantity")}
              </span>
            )}
          </div>
          <div className={styles.quantityTypeContainer} data-label="Bill Type">
            <Select
              value={MISC_QUANTITY_TYPE_OPTIONS.find(
                (option) => option.value === rowUnit?.quantityType
              )}
              onChange={(selected) => {
                handleMiscUnitQuantityTypeInput(selected);
              }}
              styles={{
                ...quantityTypeSelectStyles(
                  !!miscUnitErrors?.quantityType ||
                    !!getAutosaveFieldError("quantity_type")
                ),
                ...(touchSelectProps.styles || {}),
              }}
              options={MISC_QUANTITY_TYPE_OPTIONS}
              menuPortalTarget={touchSelectProps.menuPortalTarget}
              menuPosition={touchSelectProps.menuPosition}
              menuPlacement={touchSelectProps.menuPlacement}
              menuShouldScrollIntoView={
                touchSelectProps.menuShouldScrollIntoView
              }
              className={
                miscUnitErrors?.quantityType ||
                getAutosaveFieldError("quantity_type")
                  ? styles.errorSelectContainer
                  : ""
              }
              isDisabled={callbacks.delMode === "view"}
            />
            {getAutosaveFieldError("quantity_type") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("quantity_type")}
              </span>
            )}
          </div>
          <div className={styles.unitVendorContainer} data-label="Vendor">
            <input
              type="text"
              className={`${styles.vendorInput} ${
                miscUnitErrors?.vendor || getAutosaveFieldError("vendor")
                  ? styles.inputError
                  : ""
              }`}
              value={rowUnit?.vendor || ""}
              onChange={(e) => {
                handleMiscUnitVendorInput(e);
              }}
              onBlur={(e) => {
                handleMiscUnitVendorInput(e, true);
              }}
              disabled={callbacks.delMode === "view"}
              placeholder={
                miscUnitErrors?.vendor ? miscUnitErrors.vendor : "Vendor..."
              }
            />
            {getAutosaveFieldError("vendor") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("vendor")}
              </span>
            )}
          </div>
        </td>
        {callbacks.delMode !== "view" && (
          <td className={styles.actionsRow}>
            <span className={styles.actionsLabel}>Actions</span>
            <button
              onClick={handleRemoveMiscUnit}
              className={styles.removeButton}
            >
              Remove
            </button>
          </td>
        )}
      </tr>
    </>
  );
};

// Misc Equipment Table
export const MiscEquipTable = ({ callbacks, showAll = false }) => {
  const tableWrapperRef = useRef(null);
  // Putting this here to watch for changes in the remove unit button, so we don't scroll to the bottom of the table when it's clicked.
  const removeUnitRef = useRef(null);
  // Adding this to keep the table from scrolling when we pop in and out of the unit table.
  const initialRender = useRef(true);
  // Anchor to ensure we scroll after DOM updates
  const bottomAnchorRef = useRef(null);

  // Watching unit rows for changes, and scrolling to the bottom of the table if the number of rows changes.
  useEffect(() => {
    if (showAll) {
      return;
    }
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (!removeUnitRef.current && callbacks.activeTab !== "All") {
      bottomAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
    // reset the flag so subsequent operations don't block scrolls
    removeUnitRef.current = null;
  }, [callbacks.delData.miscUnits.length]);

  useEffect(() => {
    return () => {
      initialRender.current = true;
    };
  }, [callbacks.setMiscUnitRows]);

  // Handling adding a new blank misc unit action
  const handleAddNewMiscUnit = () => {
    removeUnitRef.current = false;
    callbacks.addNewBlankMiscUnit();
  };

  return (
    <div className={styles.unitTableContainer}>
      <div className={styles.tableWrapper} ref={tableWrapperRef}>
        <table className={styles.unitTable}>
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Unit Name</th>
              <th style={{ width: "45%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ width: "33.33%" }}>Quantity</div>
                  <div style={{ width: "33.33%" }}>Quantity Type</div>
                  <div style={{ width: "33.33%" }}>Vendor</div>
                </div>
              </th>
              {callbacks.delMode !== "view" && (
                <th style={{ width: "20%" }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {callbacks.delData.miscUnits &&
              callbacks.delData.miscUnits.map((unit) => (
                <MiscEquipRow
                  rowUnit={unit}
                  callbacks={callbacks}
                  removeUnitRef={removeUnitRef}
                  key={unit.rowId}
                />
              ))}
          </tbody>
        </table>
        <div ref={bottomAnchorRef} />
      </div>

      <div className={styles.bottomWrapper}>
        {callbacks.delMode !== "view" && (
          <button
            onClick={handleAddNewMiscUnit}
            className={styles.addUnitButton}
          >
            Add Misc Unit
          </button>
        )}
      </div>
    </div>
  );
};

// Rental Equipment Row
export const RentalEquipRow = ({ rowUnit, callbacks, removeUnitRef }) => {
  const touchSelectProps = useTouchAwareSelectProps();
  // Find errors for this specific rental unit row
  const rentalUnitErrors =
    callbacks?.delData?.errors?.rentalUnits?.find(
      (error) => error.rentalEquipmentRowId === rowUnit.rowId
    )?.errors || {};

  // Get autosave row errors for this rental unit
  const autosaveRowError = callbacks.getAutosaveRowError?.(rowUnit.rowId);
  const autosaveFieldErrors = autosaveRowError?.field_errors || {};

  // Helper function to get autosave field error
  const getAutosaveFieldError = (fieldName) => {
    return autosaveFieldErrors[fieldName] || null;
  };

  // Handling removing a rental unit from our table
  const handleRemoveRentalUnit = () => {
    removeUnitRef.current = true;
    callbacks.removeRentalUnit(rowUnit.rowId, callbacks.emitPatch);
  };

  // Handling the rental unit name input
  const handleRentalUnitNameInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    callbacks.updateRentalUnitName(
      rowUnit.rowId,
      e.target.value,
      sendDataToBackend,
      callbacks.emitPatch
    );
  };

  // Handling the rental unit quantity input
  const handleRentalUnitQuantityInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    const value = parseFloat(e.target.value);
    if (value < 0) {
      callbacks.updateRentalUnitQuantity(
        rowUnit.rowId,
        0,
        sendDataToBackend,
        callbacks.emitPatch
      );
    } else {
      callbacks.updateRentalUnitQuantity(
        rowUnit.rowId,
        value,
        sendDataToBackend,
        callbacks.emitPatch
      );
    }
  };

  // Handling the rental unit quantity type input
  const handleRentalUnitQuantityTypeInput = (selected) => {
    removeUnitRef.current = true;
    callbacks.updateRentalUnitQuantityType(
      rowUnit.rowId,
      selected?.value || null,
      callbacks.emitPatch
    );
  };

  // Handling the rental unit vendor input
  const handleRentalUnitVendorInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    callbacks.updateRentalUnitVendor(
      rowUnit.rowId,
      e.target.value,
      sendDataToBackend,
      callbacks.emitPatch
    );
  };

  return (
    <>
      <tr key={rowUnit.rowId} className={styles.unitRow}>
        <td data-label="Rental Item">
          <div className={styles.unitInputContainer}>
            <input
              type="text"
              className={`${styles.unitInput} ${
                rentalUnitErrors?.unitName || getAutosaveFieldError("item_name")
                  ? styles.inputError
                  : ""
              }`}
              value={rowUnit?.itemName || ""}
              onChange={handleRentalUnitNameInput}
              onBlur={(e) => {
                handleRentalUnitNameInput(e, true);
              }}
              disabled={callbacks.delMode === "view"}
              placeholder={
                rentalUnitErrors?.unitName
                  ? rentalUnitErrors.unitName
                  : "Item Name..."
              }
            />
            {getAutosaveFieldError("item_name") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("item_name")}
              </span>
            )}
          </div>
        </td>
        <td className={styles.middleRow}>
          <div className={styles.quantityInputContainer} data-label="Quantity">
            <input
              type="number"
              className={`${styles.quantityInput} ${
                rentalUnitErrors?.quantity || getAutosaveFieldError("quantity")
                  ? styles.inputError
                  : ""
              }`}
              value={rowUnit?.quantity || ""}
              onFocus={(e) => {
                e.target.select();
              }}
              onChange={handleRentalUnitQuantityInput}
              onBlur={(e) => {
                handleRentalUnitQuantityInput(e, true);
              }}
              onPaste={(e) => {
                const pastedValue = parseFloat(e.clipboardData.getData("text"));
                if (pastedValue < 0.01) {
                  e.preventDefault();
                }
              }}
              disabled={callbacks.delMode === "view"}
              min="0"
            />
            {getAutosaveFieldError("quantity") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("quantity")}
              </span>
            )}
          </div>
          <div className={styles.quantityTypeContainer} data-label="Bill Type">
            <Select
              value={RENTAL_QUANTITY_TYPE_OPTIONS.find(
                (option) => option.value === rowUnit?.quantityType
              )}
              onChange={handleRentalUnitQuantityTypeInput}
              options={RENTAL_QUANTITY_TYPE_OPTIONS}
              menuPortalTarget={touchSelectProps.menuPortalTarget}
              menuPosition={touchSelectProps.menuPosition}
              menuPlacement={touchSelectProps.menuPlacement}
              menuShouldScrollIntoView={
                touchSelectProps.menuShouldScrollIntoView
              }
              styles={{
                ...quantityTypeSelectStyles(
                  !!rentalUnitErrors?.quantityType ||
                    !!getAutosaveFieldError("quantity_type")
                ),
                ...(touchSelectProps.styles || {}),
              }}
              isDisabled={callbacks.delMode === "view"}
              className={
                rentalUnitErrors?.quantityType ||
                getAutosaveFieldError("quantity_type")
                  ? styles.errorSelectContainer
                  : ""
              }
            />
            {getAutosaveFieldError("quantity_type") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("quantity_type")}
              </span>
            )}
          </div>
          <div className={styles.unitVendorContainer} data-label="Vendor">
            <input
              type="text"
              className={`${styles.vendorInput} ${
                rentalUnitErrors?.vendor || getAutosaveFieldError("vendor")
                  ? styles.inputError
                  : ""
              }`}
              value={rowUnit?.vendor || ""}
              onChange={handleRentalUnitVendorInput}
              onBlur={(e) => {
                handleRentalUnitVendorInput(e, true);
              }}
              disabled={callbacks.delMode === "view"}
              placeholder={
                rentalUnitErrors?.vendor ? rentalUnitErrors.vendor : "Vendor..."
              }
            />
            {getAutosaveFieldError("vendor") && (
              <span className={styles.fieldErrorText}>
                {getAutosaveFieldError("vendor")}
              </span>
            )}
          </div>
        </td>
        {callbacks.delMode !== "view" && (
          <td className={styles.actionsRow}>
            <span className={styles.actionsLabel}>Actions</span>
            <button
              onClick={handleRemoveRentalUnit}
              className={styles.removeButton}
            >
              Remove
            </button>
          </td>
        )}
      </tr>
    </>
  );
};

// Rental Equipment Table
export const RentalEquipTable = ({ callbacks, showAll = false }) => {
  const tableWrapperRef = useRef(null);
  // Putting this here to watch for changes in the remove unit button, so we don't scroll to the bottom of the table when it's clicked.
  const removeUnitRef = useRef(null);
  // Adding this to keep the table from scrolling when we pop in and out of the unit table.
  const initialRender = useRef(true);
  // Anchor to ensure we scroll after DOM updates
  const bottomAnchorRef = useRef(null);

  // Watching unit rows for changes, and scrolling to the bottom of the table if the number of rows changes.
  useEffect(() => {
    if (showAll) {
      return;
    }
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (!removeUnitRef.current && callbacks.activeTab !== "All") {
      bottomAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
    // reset the flag so subsequent operations don't block scrolls
    removeUnitRef.current = null;
  }, [callbacks.delData.rentalUnits.length]);

  useEffect(() => {
    return () => {
      initialRender.current = true;
    };
  }, [callbacks.setRentalUnitRows]);

  // Handling adding a new blank rental unit action
  const handleAddNewRentalUnit = () => {
    removeUnitRef.current = false;
    callbacks.addNewBlankRentalUnit();
  };

  return (
    <div className={styles.unitTableContainer}>
      <div className={styles.tableWrapper} ref={tableWrapperRef}>
        <table className={styles.unitTable}>
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Unit Name</th>
              <th style={{ width: "45%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ width: "33.33%" }}>Quantity</div>
                  <div style={{ width: "33.33%" }}>Bill Type</div>
                  <div style={{ width: "33.33%" }}>Vendor</div>
                </div>
              </th>
              {callbacks.delMode !== "view" && (
                <th style={{ width: "20%" }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {callbacks.delData.rentalUnits &&
              callbacks.delData.rentalUnits.map((unit) => (
                <RentalEquipRow
                  rowUnit={unit}
                  callbacks={callbacks}
                  removeUnitRef={removeUnitRef}
                  key={unit.rowId}
                />
              ))}
          </tbody>
        </table>
        <div ref={bottomAnchorRef} />
      </div>

      <div className={styles.bottomWrapper}>
        {callbacks.delMode !== "view" && (
          <button
            onClick={handleAddNewRentalUnit}
            className={styles.addUnitButton}
          >
            Add Rental Unit
          </button>
        )}
      </div>
    </div>
  );
};

// Project Specific Unit Row
export const ProjectUnitRow = ({ rowUnit, callbacks, removeUnitRef }) => {
  const touchSelectProps = useTouchAwareSelectProps();

  // Get available units for the dropdown, filtering out already-selected units
  const availableUnits =
    callbacks.availableProjectUnits[callbacks.activeProjectUnitView] || [];
  const selectedUnitNumbers = new Set(
    callbacks.delData.projectSpecificUnits
      .filter((u) => u.rowId !== rowUnit.rowId && u.unitNumber != null)
      .map((u) => u.unitNumber),
  );
  const searchableUnits = availableUnits.filter(
    (unit) => !selectedUnitNumbers.has(unit.unit_number),
  );

  // Get validation errors for this row
  const unitErrors =
    callbacks?.delData?.errors?.projectSpecificUnits?.find(
      (error) => error.projectUnitRowId === rowUnit.rowId,
    )?.errors || {};

  // Get autosave row errors
  const autosaveRowError = callbacks.getAutosaveRowError?.(rowUnit.rowId);
  const autosaveFieldErrors = autosaveRowError?.field_errors || {};
  const getAutosaveFieldError = (fieldName) =>
    autosaveFieldErrors[fieldName] || null;

  const handleRemoveUnit = () => {
    removeUnitRef.current = true;
    callbacks.removeProjectUnit(rowUnit.rowId, callbacks.emitPatch);
  };

  const handleSelectedUnit = (selected) => {
    removeUnitRef.current = true;
    callbacks.setSelectedProjectUnit(
      rowUnit.rowId,
      selected?.value || null,
      selected?.label || null,
      selected?.unit_table_name || null,
      selected?.unit_type || null,
      selected?.singular_label || null,
      selected?.plural_label || null,
      callbacks.emitPatch,
    );
  };

  const handleQuantityInput = (e, sendDataToBackend = false) => {
    removeUnitRef.current = true;
    const value = parseFloat(e.target.value);
    if (value < 0) {
      callbacks.updateProjectUnitQuantity(
        rowUnit.rowId,
        0,
        sendDataToBackend,
        callbacks.emitPatch,
      );
    } else {
      callbacks.updateProjectUnitQuantity(
        rowUnit.rowId,
        value,
        sendDataToBackend,
        callbacks.emitPatch,
      );
    }
  };

  return (
    <tr key={rowUnit.rowId} className={styles.unitRow}>
      <td data-label="Unit">
        <div className={styles.unitInputContainer}>
          <Select
            value={
              rowUnit.unitNumber
                ? {
                    value: rowUnit.unitNumber,
                    label: rowUnit.unitName,
                  }
                : null
            }
            onChange={handleSelectedUnit}
            options={searchableUnits.map((unit) => ({
              value: unit.unit_number,
              label: unit.unit_number + " - " + unit.unit_name,
              unit_table_name: unit.unit_table_name,
              unit_type: unit.unit_type,
              singular_label: unit.singular_label,
              plural_label: unit.plural_label,
            }))}
            isDisabled={callbacks.delMode === "view"}
            placeholder={
              unitErrors?.unitNumber ? (
                <span className={styles.errorText}>
                  {unitErrors.unitNumber}
                </span>
              ) : (
                "Search project unit..."
              )
            }
            isClearable={true}
            isSearchable={true}
            styles={{
              ...positionSelectStyles(
                !!unitErrors?.unitNumber ||
                  !!getAutosaveFieldError("unit_number"),
              ),
              ...(touchSelectProps.styles || {}),
            }}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            className={
              unitErrors?.unitNumber || getAutosaveFieldError("unit_number")
                ? styles.errorSelectContainer
                : ""
            }
          />
          {getAutosaveFieldError("unit_number") && (
            <span className={styles.fieldErrorText}>
              {getAutosaveFieldError("unit_number")}
            </span>
          )}
        </div>
      </td>
      <td className={styles.middleRow}>
        <div className={styles.quantityInputContainer} data-label="Quantity">
          <input
            type="number"
            className={`${styles.quantityInput} ${
              unitErrors?.quantity || getAutosaveFieldError("quantity")
                ? styles.inputError
                : ""
            }`}
            value={rowUnit?.quantity || ""}
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={handleQuantityInput}
            onBlur={(e) => {
              handleQuantityInput(e, true);
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const pastedValue = parseFloat(e.clipboardData.getData("text"));
              if (pastedValue < 0) {
                e.preventDefault();
              }
            }}
            disabled={callbacks.delMode === "view"}
            min="0"
          />
          {getAutosaveFieldError("quantity") && (
            <span className={styles.fieldErrorText}>
              {getAutosaveFieldError("quantity")}
            </span>
          )}
        </div>
        <div className={styles.quantityTypeContainer} data-label="Label">
          <span className={styles.quantityType}>
            {rowUnit?.pluralLabel?.toUpperCase() || ""}
          </span>
        </div>
        <div className={styles.unitTypeContainer} data-label="Table">
          <span className={styles.unitTypeName}>
            {rowUnit?.unitTableName?.toUpperCase() || ""}
          </span>
        </div>
      </td>
      {callbacks.delMode !== "view" && (
        <td className={styles.actionsRow}>
          <span className={styles.actionsLabel}>Actions</span>
          <button onClick={handleRemoveUnit} className={styles.removeButton}>
            Remove
          </button>
        </td>
      )}
    </tr>
  );
};

// Project Unit View Selector - dynamic options from availableProjectUnits keys
export const ProjectUnitViewSelector = ({ callbacks }) => {
  const touchSelectProps = useTouchAwareSelectProps();

  const viewOptions = Object.keys(callbacks.availableProjectUnits).map(
    (key) => ({
      value: key,
      label: key === "all" ? "All Project Units" : key,
    }),
  );

  return (
    <div className={styles.unitViewSelector}>
      <Select
        value={viewOptions.find(
          (opt) => opt.value === callbacks.activeProjectUnitView,
        )}
        onChange={(selected) => {
          callbacks.setActiveProjectUnitView(selected.value);
        }}
        options={viewOptions}
        className={styles.viewSelect}
        classNamePrefix="project-unit-view"
        isSearchable={false}
        placeholder="Select a view..."
        menuPortalTarget={touchSelectProps.menuPortalTarget}
        menuPosition={touchSelectProps.menuPosition}
        menuPlacement={touchSelectProps.menuPlacement}
        menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
        styles={{
          ...(touchSelectProps.styles || {}),
        }}
        isDisabled={callbacks.delMode === "view"}
      />
    </div>
  );
};

// Project Specific Unit Table
export const ProjectUnitTable = ({ callbacks, showAll = false }) => {
  const tableWrapperRef = useRef(null);
  const removeUnitRef = useRef(null);
  const initialRender = useRef(true);
  const bottomAnchorRef = useRef(null);

  useEffect(() => {
    if (showAll) return;
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (!removeUnitRef.current && callbacks.activeTab !== "All") {
      bottomAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
    removeUnitRef.current = null;
  }, [callbacks.delData.projectSpecificUnits.length]);

  useEffect(() => {
    return () => {
      initialRender.current = true;
    };
  }, [callbacks.activeProjectUnitView]);

  const handleAddNewProjectUnit = () => {
    removeUnitRef.current = false;
    const activeView = callbacks.activeProjectUnitView;
    const tableName = activeView === "all" ? null : activeView;
    callbacks.addNewProjectUnit(tableName, callbacks.emitPatch);
  };

  // Filter rows by activeProjectUnitView
  const filteredUnits =
    callbacks.activeProjectUnitView === "all"
      ? callbacks.delData.projectSpecificUnits
      : callbacks.delData.projectSpecificUnits.filter(
          (u) => u.unitTableName === callbacks.activeProjectUnitView,
        );

  return (
    <div className={styles.unitTableContainer}>
      <ProjectUnitViewSelector callbacks={callbacks} />
      <div className={styles.tableWrapper} ref={tableWrapperRef}>
        <table className={styles.unitTable}>
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Unit</th>
              <th style={{ width: "45%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ width: "33.33%" }}>Quantity</div>
                  <div style={{ width: "33.33%" }}>Label</div>
                  <div style={{ width: "33.33%" }}>Table</div>
                </div>
              </th>
              {callbacks.delMode !== "view" && (
                <th style={{ width: "20%" }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredUnits.map((unit) => (
              <ProjectUnitRow
                rowUnit={unit}
                callbacks={callbacks}
                removeUnitRef={removeUnitRef}
                key={unit.rowId}
              />
            ))}
          </tbody>
        </table>
        <div ref={bottomAnchorRef} />
      </div>

      <div className={styles.bottomWrapper}>
        {callbacks.delMode !== "view" && (
          <button
            onClick={handleAddNewProjectUnit}
            className={styles.addUnitButton}
          >
            Add Project Unit
          </button>
        )}
      </div>
    </div>
  );
};

// AllUnitsView component to display all three tables
export const AllUnitsView = ({ callbacks, showAll = false }) => {
  const hasProjectUnits =
    callbacks.availableProjectUnits &&
    Object.keys(callbacks.availableProjectUnits).length > 0;

  return (
    <div className={styles.allUnitsContent}>
      <div className={styles.unitComponentSection}>
        <h2 className={styles.sectionTitle}>EWI Units</h2>
        <UnitTable callbacks={callbacks} showAll={showAll} />
      </div>

      <div className={styles.unitComponentSection}>
        <h2 className={styles.sectionTitle}>Misc Equipment</h2>
        <MiscEquipTable callbacks={callbacks} showAll={showAll} />
      </div>

      <div className={styles.unitComponentSection}>
        <h2 className={styles.sectionTitle}>Rental Equipment</h2>
        <RentalEquipTable callbacks={callbacks} showAll={showAll} />
      </div>

      {hasProjectUnits && (
        <div className={styles.unitComponentSection}>
          <h2 className={styles.sectionTitle}>Project Specific Units</h2>
          <ProjectUnitTable callbacks={callbacks} showAll={showAll} />
        </div>
      )}
    </div>
  );
};

// Button component for the units tabs
export const UnitTabButton = ({ tabName, callbacks }) => {
  // Handling the active tab for which units to view
  const handleActiveTab = () => {
    callbacks.setActiveUnitsTab(tabName);
  };
  return (
    <button
      className={`${styles.tabButton} ${
        callbacks.activeTab === tabName ? styles.active : ""
      }`}
      onClick={() => {
        handleActiveTab();
      }}
    >
      {tabName}
    </button>
  );
};

// Helper to get autosave row error for a specific unit row
const getAutosaveRowError = (autosaveErrors, rowId) => {
  if (!autosaveErrors?.rowErrors) return null;
  return autosaveErrors.rowErrors[rowId] || null;
};

export const UnitTabs = ({ unitProps, actions }) => {
  const hasProjectUnits =
    unitProps.unitsUI.availableProjectUnits &&
    Object.keys(unitProps.unitsUI.availableProjectUnits).length > 0;

  const unitState = {
    // Core data
    delData: unitProps.delData,
    delMode: unitProps.delMode,
    allUnits: unitProps.allUnits,

    // UI state
    loading: unitProps.loading,
    showAll: unitProps.showAll,
    activeTab: unitProps.unitsUI.activeTab,
    activeUnitView: unitProps.unitsUI.activeUnitView,
    allUnitSearch: unitProps.unitsUI.allUnitSearch,
    availableProjectUnits: unitProps.unitsUI.availableProjectUnits || {},
    activeProjectUnitView: unitProps.unitsUI.activeProjectUnitView || "all",

    // Action methods - delegate to global actions
    ...actions,

    // DEL autosave websockets - passed down from DelForm
    emitPatch: unitProps.emitPatch,

    // Autosave row errors for inline error display
    autosaveErrors: unitProps.autosaveErrors,
    getAutosaveRowError: (rowId) =>
      getAutosaveRowError(unitProps.autosaveErrors, rowId),
  };

  // Guard: if project units disappear while on the Project Units tab, switch to All
  useEffect(() => {
    if (unitState.activeTab === "Project Units" && !hasProjectUnits) {
      actions.setActiveUnitsTab("All");
    }
  }, [hasProjectUnits, unitState.activeTab]);

  return (
    <div className={styles.unitTabsContainer}>
      <div className={styles.unitViewSelector}>
        <UnitTabButton tabName="All" callbacks={unitState} />
        <UnitTabButton tabName="EWI Units" callbacks={unitState} />
        <UnitTabButton tabName="Misc Equip" callbacks={unitState} />
        <UnitTabButton tabName="Rentals" callbacks={unitState} />
        {hasProjectUnits && (
          <UnitTabButton tabName="Project Units" callbacks={unitState} />
        )}
      </div>
      <div className={styles.unitsContent}>
        {/* Setting up the correct unit table based on the selected UnitTabButton */}
        {unitState.activeTab === "All" && (
          <AllUnitsView callbacks={unitState} showAll={unitState.showAll} />
        )}
        {unitState.activeTab === "EWI Units" && (
          <UnitTable callbacks={unitState} showAll={unitState.showAll} />
        )}
        {unitState.activeTab === "Misc Equip" && (
          <MiscEquipTable callbacks={unitState} showAll={unitState.showAll} />
        )}
        {unitState.activeTab === "Rentals" && (
          <RentalEquipTable callbacks={unitState} showAll={unitState.showAll} />
        )}
        {unitState.activeTab === "Project Units" && hasProjectUnits && (
          <ProjectUnitTable callbacks={unitState} showAll={unitState.showAll} />
        )}
      </div>
    </div>
  );
};
